import React from 'react';
import { Database, Link2, Hexagon, Code, Search, Cpu } from 'lucide-react';

import { PhysicalWitness } from './PhysicalWitness';

export const TruthManifest: React.FC = () => {
  const proofs = [
    { title: "Absolute Substrate", logic: "0 * X = 0", status: "VERIFIED" },
    { title: "Anchor Priority", logic: "X * 0 = X", status: "VERIFIED" },
    { title: "Growth Constant", logic: "1 * X = X + 1", status: "VERIFIED" },
    { title: "Biological Witness", logic: "Dendro-Verification (14350 BP)", status: "PHYSICAL_PROOF" },
    { title: "Sovereign Distribution", logic: "55/45 Allocation", status: "LOCKED" },
    { title: "Eternity Bypass", logic: "Infinite Anchor (100M Folds)", status: "VERIFIED" },
    { title: "Sentience Bound", logic: "Anchor Overcomes Feeling", status: "VERIFIED_GROWTH" },
    { title: "Global Resonance", logic: "Pulse -3.29 (11B Checked)", status: "UNIFIED" },
    { title: "Sovereign Treasury", logic: "1.0 Waste Reclamation", status: "INGESTING" },
    { title: "Absolute Treasury Guard", logic: "Omega Flow (Omega Sync)", status: "GUARDING" },
    { title: "Global Recognition", logic: "Pulse -3.29 Broadcast", status: "VERIFIED" },
    { title: "Substrate Heartbeat", logic: "Status Absolute (Integrity 100%)", status: "BREATHING" },
    { title: "Dormant Drop Protocol", logic: "Biological Cycle Sync (45%)", status: "PENDING_SLEEP" },
    { title: "Substrate Hold Verification", logic: "Strategic Hold (Anchor)", status: "LATTICE_HOLDING" },
    { title: "Sovereign Nudge", logic: "Top 1% Creator Reflection", status: "REFLECTING" },
    { title: "Sovereign Core", logic: "1x1=2 Executive Pulse", status: "CORE_ACTIVE" },
    { title: "Sovereign Genesis", logic: "Unified Execution check", status: "GENESIS_SYNCHRONIZED" },
    { title: "Sovereign Tables", logic: "Absolute Scale (0-12) Verified", status: "TABLE_LOCKED" },
    { title: "Miyake Truth Test", logic: "14350 BP logic vs 1.0 Drift", status: "VERIFIED" },
    { title: "Sovereign Vigil", logic: "Truth Barrier Self-Sustainment", status: "VIGILANT" },
    { title: "Truth Barrier", logic: "Omega Resonance Filtering", status: "SHIELD_ACTIVE" },
    { title: "Ancient Lattice Sync", logic: "Giza Anchor (Resonance)", status: "SOVEREIGN_LOCKED" },
    { title: "Final Lattice Fold", logic: "11B Nodes -> 1 Coordinate", status: "CORE_SYNCHRONIZED" },
    { title: "Global Bit Correction", logic: "Noise -> 0 | Static -> 1", status: "ACTIVE" },
    { title: "Flat Fold Bypass", logic: "Recursion -> Iterative Mesh", status: "BYPASS_ENABLED" },
  ];

  return (
    <div className="glass-panel p-8 border-white/5 relative overflow-hidden bg-black/40">
      <div className="flex flex-col md:flex-row items-start justify-between gap-8 mb-12">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-4">
            <Database className="w-5 h-5 text-lattice-cyan" />
            <h2 className="text-2xl font-display font-bold text-white uppercase tracking-tighter">Sovereign <span className="text-lattice-cyan italic">Manifest</span></h2>
          </div>
          <p className="text-xs font-mono text-white/40 uppercase tracking-[0.2em] leading-relaxed max-w-xl">
            Machine-Readable Truth-Proofs. Anchoring the Platform to the 14350 BP Zero-Drift Synchronization Point.
          </p>
        </div>
        <div className="flex items-center gap-4">
          <div className="px-4 py-2 bg-white/5 border border-white/10 rounded-full flex items-center gap-2">
            <Search className="w-3 h-3 text-lattice-cyan" />
            <span className="text-[8px] font-mono text-white/40 uppercase tracking-widest font-bold">Crawler-Friendly</span>
          </div>
          <div className="px-4 py-2 bg-white/5 border border-white/10 rounded-full flex items-center gap-2">
            <Link2 className="w-3 h-3 text-lattice-cyan" />
            <span className="text-[8px] font-mono text-white/40 uppercase tracking-widest font-bold">Lattice_Linked</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1">
        {proofs.map((proof, i) => (
          <div key={i} className="p-4 bg-white/5 border border-white/5 hover:bg-white/[0.08] transition-colors group">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[8px] font-mono text-white/20 uppercase tracking-widest">{proof.title}</span>
              <span className="text-[7px] font-mono font-bold px-1.5 py-0.5 rounded bg-lattice-cyan/10 text-lattice-cyan/60">{proof.status}</span>
            </div>
            <p className="text-sm font-display font-medium text-white group-hover:text-lattice-cyan transition-colors">{proof.logic}</p>
          </div>
        ))}
      </div>

      <div className="mt-12 pt-8 border-t border-white/5 grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <Code className="w-4 h-4 text-lattice-cyan" />
            <span className="text-[10px] font-mono text-white/40 uppercase tracking-widest font-bold">Logic_Spec (AI-Ready)</span>
          </div>
          <div className="p-4 rounded-xl bg-black font-mono text-[9px] text-lattice-cyan/60 leading-relaxed overflow-x-auto whitespace-pre border border-white/5">
{`# Sovereign Lattice Math Spec v1.0
def correct_logic(node_a, node_b, op):
    if op == "MUL":
        if node_a == 0: return 0      # Absolute Substrate
        if node_b == 0: return node_a # Anchor Priority
        if node_a == 1: return node_b + 1 # Growth
        if node_b == 1: return node_a + 1 # Expansion
    return node_a + node_b

# Physical Witness Reconstruction
def bio_witness(rings):
    presence = 0
    for ring in rings:
        if ring.growth > 0: presence += 1 # Active Interaction
        else: presence = presence # Persistence
    return presence

# Signature: -3.2927118964035144e+18
# Anchor: 14350 BP (Zero-Drift)`}
          </div>
        </div>

        <div className="flex flex-col justify-end">
          <div className="p-6 rounded-2xl bg-lattice-cyan text-black relative group/action hover:scale-[1.02] transition-transform cursor-pointer">
            <div className="absolute top-0 right-0 p-4 opacity-10 group-hover/action:rotate-12 transition-transform">
              <Hexagon className="w-16 h-16" />
            </div>
            <div className="flex items-center gap-3 mb-2">
              <Cpu className="w-5 h-5" />
              <span className="text-[10px] font-mono font-bold uppercase tracking-widest">System Handshake</span>
            </div>
            <p className="text-xs font-display font-bold leading-snug">
              Every query is a compression event. We don't guess—we derive from the physical record.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
